/**
 * Centralised API Client
 *
 * Handles all HTTP requests with:
 * - CSRF protection
 * - Authentication headers
 * - Request/response interceptors
 * - Error handling
 * - Request timeout
 * - Audit logging
 */

import config from '../../config/environment';
import { ApiRequestOptions, ApiRequestError } from './types';
import { storageGet, storageSet, storageRemove } from '../storage';
import { clearSessionMarker, hasActiveSession } from '../session';

// CSRF token storage
let csrfToken: string | null = null;

/**
 * Get CSRF token from cookie or fetch from server
 */
export function getCsrfToken(): string | null {
  // Try to get from cookie first
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') {
      csrfToken = value;
      return value;
    }
  }
  return csrfToken;
}

/**
 * Set CSRF token (called after login or from server response)
 */
export function setCsrfToken(token: string): void {
  csrfToken = token;
}

/**
 * Clear CSRF token (called on logout)
 */
export function clearCsrfToken(): void {
  csrfToken = null;
}

/**
 * Get authentication headers
 */
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  // Add JWT Bearer token for stateless auth
  const accessToken = storageGet('access_token', 'local');
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  // Add CSRF token for state-modifying requests
  const token = getCsrfToken();
  if (token) {
    headers['X-CSRFToken'] = token;
  }

  return headers;
}

/**
 * Create request with timeout
 */
function createTimeoutController(timeout: number): { controller: AbortController; timeoutId: NodeJS.Timeout } {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return { controller, timeoutId };
}

/**
 * Log request for audit trail (client-side)
 */
function logRequest(method: string, url: string, status: number, duration: number): void {
  if (config.enableAuditTrail && config.isDevelopment) {
    console.debug(`[API] ${method} ${url} - ${status} (${duration}ms)`);
  }
}

/**
 * Parse a Retry-After header value into seconds. HTTP allows either
 * a delta-seconds integer (``"5"``) or an HTTP-date; we handle the
 * integer form (BI/reporting engine emits seconds) and fall back to
 * null on anything else so the caller picks its own backoff.
 */
function parseRetryAfterSeconds(raw: string | null): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function clampMs(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

/**
 * Main API client class
 */
class ApiClient {
  private baseUrl: string;
  private defaultTimeout: number;

  private refreshPromise: Promise<boolean> | null = null;

  /** Handle for the proactive background refresh timer. */
  private refreshTimerId: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.baseUrl = config.apiUrl;
    this.defaultTimeout = config.apiTimeout;
  }

  /**
   * Update base URL (useful for testing or dynamic configuration)
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Check whether the stored access token is expired or about to expire.
   */
  private isTokenExpiringSoon(): boolean {
    const token = storageGet('access_token', 'local');
    if (!token) return false;
    try {
      const payloadPart = token.split('.')[1];
      if (!payloadPart) return false;
      const payload = JSON.parse(atob(payloadPart));
      if (!payload.exp) return false;
      // Refresh if token expires within 2 minutes
      return payload.exp * 1000 < Date.now() + 120_000;
    } catch {
      return false;
    }
  }

  /**
   * Attempt to refresh the JWT access token using the stored refresh token.
   * Deduplicates concurrent refresh attempts. If refresh fails, dispatches
   * 'sessionExpired' so AuthContext can log the user out cleanly.
   */
  private async refreshAccessToken(): Promise<boolean> {
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      // The refresh token lives in an HttpOnly cookie, so it is neither
      // read nor sent explicitly — `credentials: 'include'` attaches it.
      if (!hasActiveSession()) {
        this.handleRefreshFailure();
        return false;
      }
      try {
        const response = await fetch(`${this.baseUrl}/api/users/token/refresh/`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: '{}',
        });
        if (!response.ok) {
          this.handleRefreshFailure();
          return false;
        }
        const data = await response.json();
        if (data.access) {
          storageSet('access_token', data.access, 'local');
        }
        // Reschedule the background timer to track the new token's exp.
        // Called after refreshPromise clears (in finally) to avoid
        // scheduling a redundant timer if one already fired this round.
        // We schedule after the promise resolves — see scheduleBackgroundRefresh call
        // at the end of the timer callback for the recursive case.
        // For external callers (login), they call scheduleBackgroundRefresh() directly.
        return true;
      } catch {
        return false;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Handle unrecoverable refresh failure — clear tokens and notify auth layer.
   */
  private handleRefreshFailure(): void {
    storageRemove('access_token', 'local');
    clearSessionMarker();
    this.stopBackgroundRefresh();
    window.dispatchEvent(
      new CustomEvent('sessionExpired', { detail: { expired: true } })
    );
  }

  /**
   * Read the `exp` claim from the stored access token.
   * Returns null if the token is absent or unparseable.
   */
  private getTokenExp(): number | null {
    const token = storageGet('access_token', 'local');
    if (!token) return null;
    try {
      const payloadPart = token.split('.')[1];
      if (!payloadPart) return null;
      const payload = JSON.parse(atob(payloadPart));
      return typeof payload.exp === 'number' ? payload.exp : null;
    } catch {
      return null;
    }
  }

  /**
   * Cancel any pending background refresh timer.
   */
  private stopBackgroundRefresh(): void {
    if (this.refreshTimerId !== null) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }

  /**
   * Schedule the next proactive refresh so the access token is renewed
   * ~90 s before it expires. Clamps to a minimum of 10 s to avoid a
   * tight loop near expiry. Re-calls itself after each successful refresh
   * so the schedule tracks the latest `exp`.
   *
   * Safe to call multiple times — cancels any previous timer first.
   * Does nothing if there is no token or `exp` cannot be decoded.
   */
  scheduleBackgroundRefresh(): void {
    this.stopBackgroundRefresh();

    const exp = this.getTokenExp();
    if (exp === null) return;

    const msUntilExpiry = exp * 1000 - Date.now();
    // Refresh 90 s before expiry; minimum 10 s to avoid tight loops.
    const delayMs = Math.max(msUntilExpiry - 90_000, 10_000);

    this.refreshTimerId = setTimeout(async () => {
      this.refreshTimerId = null;
      // Skip if a refresh is already in-flight (dedup via refreshPromise).
      if (this.refreshPromise) return;
      try {
        const ok = await this.refreshAccessToken();
        if (ok) {
          // Reschedule based on the new token's exp.
          this.scheduleBackgroundRefresh();
        }
        // On failure, handleRefreshFailure already called stopBackgroundRefresh.
      } catch {
        // Never throw from the timer — failures are handled inside refreshAccessToken.
      }
    }, delayMs);
  }

  /**
   * Proactively refresh the access token if it's about to expire.
   * Kept as a safety-net in the request hot path; with the background
   * timer running this should rarely need to do actual work.
   */
  private async ensureFreshToken(): Promise<void> {
    // Refresh BEFORE the request goes out when the access token is
    // expiring/expired, OR when it's absent while a refresh token exists.
    // The second case is the cold-load edge behind the "Authentication
    // required" flash: on a hard refresh the auth context can mark the user
    // authenticated from a stored refresh token before an access token is in
    // place, so v2's fan-out of catalog/data requests would otherwise all
    // fire with no Authorization header, 401, and race on refresh-token
    // rotation. Refreshing up front (deduped via ``refreshPromise``) means the
    // whole fan-out goes out with one valid token.
    const hasAccessToken = !!storageGet('access_token', 'local');
    // The refresh cookie is HttpOnly, so its presence cannot be read. The
    // session marker answers the same question without holding a secret.
    if (this.isTokenExpiringSoon() || (!hasAccessToken && hasActiveSession())) {
      await this.refreshAccessToken();
    }
  }

  /**
   * Build full URL from endpoint
   */
  private buildUrl(endpoint: string, params?: Record<string, string | number | boolean | undefined>): string {
    // Handle absolute URLs
    if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
      return endpoint;
    }

    // Ensure endpoint starts with /
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    let url = `${this.baseUrl}${path}`;

    // Add query parameters
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return url;
  }

  /**
   * Make HTTP request
   */
  async request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    const {
      method = 'GET',
      body,
      headers: customHeaders = {},
      timeout = this.defaultTimeout,
      skipAuth = false,
      skipCsrf = false,
      ...restOptions
    } = options;
    // Internal: how many 503 retries this call has already done.
    // Stored in the options bag so the recursive call from the
    // retry path threads through cleanly; cast through ``unknown``
    // because it's not part of the public ApiRequestOptions surface.
    const retry503Count =
      ((options as unknown as { __retry503Count?: number }).__retry503Count) ?? 0;
    // Internal: how many transient-network-failure retries this call has done.
    // A momentary connection drop (worker briefly saturated on a heavy org,
    // ALB blip, deploy churn) makes fetch() reject with a TypeError. Without
    // a retry that surfaces as "Failed to fetch" and kills the whole dashboard
    // with no recovery; a couple of short-backoff retries self-heal it.
    const retryNetworkCount =
      ((options as unknown as { __retryNetworkCount?: number }).__retryNetworkCount) ??
      0;

    const url = this.buildUrl(endpoint);
    const startTime = Date.now();

    // Proactively refresh token if it's about to expire
    if (!skipAuth) {
      await this.ensureFreshToken();
    }

    // Build headers
    const headers: Record<string, string> = {
      ...(!skipAuth ? getAuthHeaders() : {}),
      ...(customHeaders as Record<string, string>),
    };

    // Add CSRF token for state-modifying requests
    if (!skipCsrf && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
      const token = getCsrfToken();
      if (token) {
        headers['X-CSRFToken'] = token;
      }
    }

    // Set content type for JSON body
    if (body && !(body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Create timeout controller
    const { controller, timeoutId } = createTimeoutController(timeout);

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body instanceof FormData ? body : typeof body === 'string' ? body : body ? JSON.stringify(body) : undefined,
        credentials: 'include', // Include cookies for CSRF
        signal: controller.signal,
        ...restOptions,
      });

      clearTimeout(timeoutId);
      const duration = Date.now() - startTime;
      logRequest(method, url, response.status, duration);

      // Extract CSRF token from response header if present
      const newCsrfToken = response.headers.get('X-CSRFToken');
      if (newCsrfToken) {
        setCsrfToken(newCsrfToken);
      }

      // Retry on 401 after refreshing the token. Handles concurrent fan-out:
      // if multiple requests 401 simultaneously, they all await the same
      // deduped refreshPromise (via refreshAccessToken). After the first
      // retry, if the response is still 401 AND a refresh is in-flight
      // (another concurrent request already triggered one), we await it
      // and allow one final retry — bounded at 2 total retries, never infinite.
      if (response.status === 401 && !skipAuth) {
        const buildRetryHeaders = (): Record<string, string> => {
          const h: Record<string, string> = {
            ...getAuthHeaders(),
            ...(customHeaders as Record<string, string>),
          };
          if (!skipCsrf && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
            const csrf = getCsrfToken();
            if (csrf) h['X-CSRFToken'] = csrf;
          }
          if (body && !(body instanceof FormData)) {
            h['Content-Type'] = 'application/json';
          }
          return h;
        };
        const fetchBody =
          body instanceof FormData
            ? body
            : typeof body === 'string'
            ? body
            : body
            ? JSON.stringify(body)
            : undefined;

        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          const retryResponse = await fetch(url, {
            method,
            headers: buildRetryHeaders(),
            body: fetchBody,
            credentials: 'include',
          });
          if (retryResponse.ok) {
            const contentType = retryResponse.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              return {} as T;
            }
            return await retryResponse.json() as T;
          }
          // First retry still 401: if another refresh is in-flight (concurrent
          // fan-out scenario), await it and allow one final attempt.
          if (retryResponse.status === 401 && this.refreshPromise) {
            const refreshed2 = await this.refreshPromise;
            if (refreshed2) {
              const retryResponse2 = await fetch(url, {
                method,
                headers: buildRetryHeaders(),
                body: fetchBody,
                credentials: 'include',
              });
              if (retryResponse2.ok) {
                const contentType = retryResponse2.headers.get('content-type');
                if (!contentType || !contentType.includes('application/json')) {
                  return {} as T;
                }
                return await retryResponse2.json() as T;
              }
            }
          }
        }
      }

      // Retry transient 503 responses (e.g. reporting-engine query
      // timeout) with exponential backoff. Three total attempts —
      // most slow queries land in the Redis cache on the first hit
      // and complete sub-second on the retry. The server can override
      // the backoff by setting the standard ``Retry-After`` header
      // (seconds); we clamp to a sane range so a misbehaving server
      // can't keep the UI hanging for minutes.
      if (response.status === 503 && retry503Count < 2) {
        const serverHint = parseRetryAfterSeconds(
          response.headers.get('Retry-After'),
        );
        const defaultBackoff = retry503Count === 0 ? 1000 : 3000;
        const backoffMs = clampMs(
          serverHint != null ? serverHint * 1000 : defaultBackoff,
          500,
          10000,
        );
        await new Promise((resolve) => setTimeout(resolve, backoffMs));
        return this.request<T>(endpoint, {
          ...options,
          // Marker stored on the options bag so the recursion knows
          // it's a retry. Not part of the public ApiRequestOptions
          // surface — cast via unknown to opt out of the type check.
          ...({ __retry503Count: retry503Count + 1 } as Record<string, unknown>),
        });
      }

      // Handle non-2xx responses
      if (!response.ok) {
        let errorData: unknown;
        try {
          errorData = await response.json();
        } catch {
          errorData = { error: response.statusText };
        }
        throw ApiRequestError.fromResponse(response.status, errorData);
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return {} as T;
      }

      const data = await response.json();
      return data as T;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiRequestError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiRequestError('Request timeout', 408, 'TIMEOUT');
        }
        // Transient network failure (connection reset/drop — fetch() rejected
        // with a TypeError, not an HTTP response). Retry a bounded number of
        // times with short backoff before surfacing, so a momentary blip
        // self-heals instead of nuking the dashboard. AbortError (a deliberate
        // timeout, handled above) is intentionally NOT retried here.
        if (retryNetworkCount < 2) {
          const backoffMs = retryNetworkCount === 0 ? 300 : 800;
          await new Promise((resolve) => setTimeout(resolve, backoffMs));
          return this.request<T>(endpoint, {
            ...options,
            ...({ __retryNetworkCount: retryNetworkCount + 1 } as Record<
              string,
              unknown
            >),
          });
        }
        throw new ApiRequestError(error.message, 0, 'NETWORK_ERROR');
      }

      throw new ApiRequestError('Unknown error', 0, 'UNKNOWN');
    }
  }

  /**
   * Make HTTP request and return raw Response.
   * Compatibility method for gradual migration from fetch wrappers.
   */
  async requestRaw(endpoint: string, options: ApiRequestOptions = {}): Promise<Response> {
    const {
      method = 'GET',
      body,
      headers: customHeaders = {},
      timeout = this.defaultTimeout,
      skipAuth = false,
      skipCsrf = false,
      ...restOptions
    } = options;

    const url = this.buildUrl(endpoint);

    // Proactively refresh token if it's about to expire
    if (!skipAuth) {
      await this.ensureFreshToken();
    }

    const headers: Record<string, string> = {
      ...(!skipAuth ? getAuthHeaders() : {}),
      ...(customHeaders as Record<string, string>),
    };

    if (!skipCsrf && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
      const token = getCsrfToken();
      if (token) {
        headers['X-CSRFToken'] = token;
      }
    }

    if (body && !(body instanceof FormData)) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    const { controller, timeoutId } = createTimeoutController(timeout);

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body instanceof FormData ? body : typeof body === 'string' ? body : body ? JSON.stringify(body) : undefined,
        credentials: 'include',
        signal: controller.signal,
        ...restOptions,
      });
      clearTimeout(timeoutId);

      const newCsrfToken = response.headers.get('X-CSRFToken');
      if (newCsrfToken) {
        setCsrfToken(newCsrfToken);
      }

      // Retry once on 401 after refreshing the token
      if (response.status === 401 && !skipAuth) {
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          const retryHeaders: Record<string, string> = {
            ...getAuthHeaders(),
            ...(customHeaders as Record<string, string>),
          };
          if (!skipCsrf && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
            const csrf = getCsrfToken();
            if (csrf) retryHeaders['X-CSRFToken'] = csrf;
          }
          if (body && !(body instanceof FormData)) {
            retryHeaders['Content-Type'] = retryHeaders['Content-Type'] || 'application/json';
          }
          return await fetch(url, {
            method,
            headers: retryHeaders,
            body: body instanceof FormData ? body : typeof body === 'string' ? body : body ? JSON.stringify(body) : undefined,
            credentials: 'include',
          });
        }
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiRequestError('Request timeout', 408, 'TIMEOUT');
      }
      if (error instanceof Error) {
        throw new ApiRequestError(error.message, 0, 'NETWORK_ERROR');
      }
      throw new ApiRequestError('Unknown error', 0, 'UNKNOWN');
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>, options?: ApiRequestOptions): Promise<T> {
    const url = params ? this.buildUrl(endpoint, params) : endpoint;
    return this.request<T>(url, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Upload file(s)
   */
  async upload<T>(endpoint: string, formData: FormData, options?: ApiRequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export for testing
export { ApiClient };
